import { useState } from 'react'
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, settingsApi } from '../lib/api'

function safeSlice(value: unknown, start?: number, end?: number): string {
  return typeof value === 'string' ? value.slice(start, end) : ''
}

function shortId(id?: string | null): string {
  return typeof id === 'string' && id.length > 0 ? id.slice(0, 8) : 'UNKNOWN'
}

const STATUS_COLORS: Record<string, string> = {
  ASSIGNED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-orange-100 text-orange-800',
  STARTED: 'bg-orange-100 text-orange-800',
  PICKED_UP: 'bg-amber-100 text-amber-800',
  IN_TRANSIT: 'bg-orange-100 text-orange-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

const STATUS_STEPS = ['ASSIGNED', 'IN_PROGRESS', 'DELIVERED']

function StepIndicator({ status }: { status: string }) {
  const steps = ['Assigned', 'Picked Up', 'Delivered']
  const activeIdx = status === 'DELIVERED' ? 2 : status === 'IN_PROGRESS' || status === 'STARTED' || status === 'PICKED_UP' ? 1 : 0
  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
            i <= activeIdx ? 'bg-blue-700 text-white' : 'bg-gray-200 text-gray-500'
          }`}>{i < activeIdx ? '✓' : i + 1}</div>
          <span className={`text-xs ${i <= activeIdx ? 'text-blue-700 font-medium' : 'text-gray-400'}`}>{s}</span>
          {i < steps.length - 1 && <div className={`h-0.5 w-8 ${i < activeIdx ? 'bg-blue-700' : 'bg-gray-200'}`} />}
        </div>
      ))}
    </div>
  )
}

export default function DriverJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()

  const [selectedPodFile, setSelectedPodFile] = useState<File | null>(null)
  const [notes, setNotes] = useState('')
  const [damageNote, setDamageNote] = useState('')
  const [showDeliveryForm, setShowDeliveryForm] = useState(false)
  const [podUploadMessage, setPodUploadMessage] = useState<string | null>(null)

  const { data: job, isLoading, error } = useQuery({
    queryKey: ['driver-job', jobId],
    queryFn: async () => { const r = await api.jobs.get(jobId!, { viewer: 'driver' }); return r.data },
    enabled: !!jobId,
    retry: false,
    refetchInterval: (query) => query.state.data ? 10000 : false,
  })

  const { data: equipment } = useQuery({
    queryKey: ['driver-job-equipment', jobId],
    queryFn: async () => (await settingsApi.getEquipment()).data.assignment,
    refetchInterval: 30000,
  })

  const pickupMut = useMutation({
    mutationFn: () => api.jobs.pickup(jobId!, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['driver-job', jobId] })
      qc.invalidateQueries({ queryKey: ['driver-jobs'] })
      setNotes('')
    },
  })

  const podUploadMut = useMutation({
    mutationFn: async () => {
      if (!selectedPodFile) throw new Error('Select a POD file first')
      return api.jobs.uploadPod(jobId!, selectedPodFile)
    },
    onSuccess: async (response) => {
      setPodUploadMessage(`Uploaded ${response.data.file.filename}`)
      setSelectedPodFile(null)
      await qc.invalidateQueries({ queryKey: ['driver-job', jobId] })
      await qc.invalidateQueries({ queryKey: ['driver-jobs'] })
    },
  })

  const deliverMut = useMutation({
    mutationFn: () => api.jobs.deliver(jobId!, {
      podUrl: job.proofOfDeliveryUrl || `/jobs/${jobId}/pod`,
      notes: [notes, damageNote].filter(Boolean).join(' | ') || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['driver-job', jobId] })
      qc.invalidateQueries({ queryKey: ['driver-jobs'] })
      setShowDeliveryForm(false)
    },
  })

  const recoveryPath = location.pathname.startsWith('/driver/') ? '/driver/jobs' : '/dispatcher'
  if (isLoading) return <div className="container mx-auto p-6 text-gray-400">Loading driver job&</div>
  if (error) return (
    <div className="container mx-auto p-6 text-red-600">
      <p>Unable to load driver job.</p>
      <Link to={recoveryPath} className="mt-3 inline-block text-sm font-medium text-blue-700 underline">
        Return to {recoveryPath === '/driver/jobs' ? 'My Jobs' : 'Dispatcher'}
      </Link>
    </div>
  )
  if (!job) return (
    <div className="container mx-auto p-6 text-red-600">
      <p>Driver job not found.</p>
      <Link to={recoveryPath} className="mt-3 inline-block text-sm font-medium text-blue-700 underline">
        Return to {recoveryPath === '/driver/jobs' ? 'My Jobs' : 'Dispatcher'}
      </Link>
    </div>
  )

  const jobStatus = typeof job.status === 'string' ? job.status : 'UNKNOWN'
  const jobTitle = typeof job.jobNumber === 'string' && job.jobNumber.length > 0
    ? job.jobNumber
    : `Job ${shortId(job.id)}`
  const loadIdDisplay = shortId(job.loadId)
  const podUploaded = Boolean(job.proofOfDeliveryUrl)
  const podUploadedAt = job.proofOfDeliveryUploadedAt ? new Date(job.proofOfDeliveryUploadedAt).toLocaleString() : null
  const podSizeText = typeof job.proofOfDeliverySizeBytes === 'number'
    ? `${(job.proofOfDeliverySizeBytes / (1024 * 1024)).toFixed(1)} MB`
    : null
  const dispatchUnits = Array.isArray((job as any).dispatchUnits) ? (job as any).dispatchUnits : []
  const assignedTripUnit = dispatchUnits[0] ?? null

  const canPickup = jobStatus === 'ASSIGNED' || jobStatus === 'STARTED'
  const canDeliver = jobStatus === 'IN_PROGRESS' || jobStatus === 'PICKED_UP' || jobStatus === 'IN_TRANSIT'
  const isDelivered = jobStatus === 'DELIVERED'

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate('/driver/jobs')} className="text-gray-400 hover:text-gray-700 text-sm">← My Jobs</button>
        <h1 className="text-xl font-bold text-gray-900">{jobTitle}</h1>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[jobStatus] ?? 'bg-gray-100'}`}>
          {jobStatus}
        </span>
      </div>

      <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-blue-900">Driver Workflow</h2>
            <p className="text-xs text-blue-700 mt-1">Run the job in order: review docs, load trailer, then complete POD.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to={`/driver/jobs/${jobId}/trailer`} className="rounded-lg border border-blue-300 bg-white px-3 py-2 font-semibold text-blue-800 hover:bg-blue-100">
              Open Trailer Loader
            </Link>
            <Link to={`/jobs/${jobId}/pod`} className="rounded-lg border border-blue-300 bg-white px-3 py-2 font-semibold text-blue-800 hover:bg-blue-100">
              Upload / View POD
            </Link>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-5 mb-4">
        <h2 className="font-semibold text-gray-800 mb-3">Assigned Unit</h2>
        {equipment || assignedTripUnit ? (
          <div className="grid gap-3 sm:grid-cols-2 text-sm">
            {assignedTripUnit && (
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Assigned Vehicle</div>
                <div className="font-medium text-gray-900">{[assignedTripUnit.year, assignedTripUnit.make, assignedTripUnit.model].filter(Boolean).join(' ') || 'Vehicle assigned'}</div>
                {assignedTripUnit.vin && <div className="text-xs text-gray-500 mt-1">VIN {assignedTripUnit.vin}</div>}
                {assignedTripUnit.trailerSlot && <div className="text-xs text-gray-500 mt-1">Trailer position: {assignedTripUnit.trailerSlot}</div>}
              </div>
            )}
            {equipment && <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tractor</div>
              <div className="font-medium text-gray-900">{equipment.tractorManufacturer && equipment.tractorModel ? `${equipment.tractorManufacturer} ${equipment.tractorModel}` : 'Assigned tractor on file'}</div>
            </div>}
            {equipment && <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Trailer</div>
              <div className="font-medium text-gray-900">{equipment.trailerManufacturer && equipment.trailerModel ? `${equipment.trailerManufacturer} ${equipment.trailerModel}` : 'Assigned trailer on file'}</div>
            </div>}
            {equipment?.unitLabel && (
              <div className="sm:col-span-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Unit Label</div>
                <div className="font-semibold text-blue-800">{equipment.unitLabel}</div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No equipment assignment found yet.</p>
        )}
      </div>

      <StepIndicator status={jobStatus} />

      {/* Job Info */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <h2 className="font-semibold text-gray-800 mb-3">Job Details</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Load ID</span>
            <span className="font-mono text-gray-700">{loadIdDisplay}</span>
          </div>
          {job.pickupConfirmedAt && (
            <div className="flex justify-between">
              <span className="text-gray-500">Picked Up</span>
              <span className="text-gray-700">{new Date(job.pickupConfirmedAt).toLocaleString()}</span>
            </div>
          )}
          {job.deliveryConfirmedAt && (
            <div className="flex justify-between">
              <span className="text-gray-500">Delivered</span>
              <span className="text-green-700 font-medium">{new Date(job.deliveryConfirmedAt).toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between items-start gap-3">
            <span className="text-gray-500">POD</span>
            <div className="text-right max-w-[240px]">
              <div className={`text-xs font-medium ${podUploaded ? 'text-green-700' : 'text-gray-500'}`}>
                {podUploaded ? 'POD: Uploaded' : 'POD: Missing'}
              </div>
              {job.proofOfDeliveryFilename && <div className="text-xs text-gray-700 truncate">{job.proofOfDeliveryFilename}</div>}
              {podSizeText && <div className="text-xs text-gray-400">{podSizeText}</div>}
              {podUploadedAt && <div className="text-xs text-gray-400">Uploaded {podUploadedAt}</div>}
              {podUploaded && job.proofOfDeliveryUrl && (
                <a href={job.proofOfDeliveryUrl} className="text-blue-700 underline text-xs">Open POD</a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-5 mb-4">
        <h2 className="font-semibold text-gray-800 mb-3">Document Packet</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to={`/jobs/${jobId}/dispatch-sheet`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">Dispatch Sheet</Link>
          <Link to={`/jobs/${jobId}/bol`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">BOL</Link>
          <Link to={`/jobs/${jobId}/pod`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">Upload / View POD</Link>
          <Link to={`/driver/jobs/${jobId}/trailer`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">Open Trailer Loader</Link>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-5 mb-4">
        <h2 className="font-semibold text-gray-800 mb-3">Upload Proof of Delivery</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">POD file</label>
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              onChange={e => {
                setSelectedPodFile(e.target.files?.[0] ?? null)
                setPodUploadMessage(null)
              }}
              className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP, or PDF. Mobile browsers may open the camera.</p>
          </div>
          {selectedPodFile && (
            <div className="text-sm text-gray-700 bg-gray-50 border rounded-lg p-3">
              Selected: <span className="font-medium">{selectedPodFile.name}</span>
              <div className="text-xs text-gray-400">
                {(selectedPodFile.size / (1024 * 1024)).toFixed(1)} MB · {selectedPodFile.type || 'unknown type'}
              </div>
            </div>
          )}
          {podUploaded && !selectedPodFile && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
              POD already attached. Upload a new file to replace it.
            </div>
          )}
          {podUploadMessage && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{podUploadMessage}</div>
          )}
          {podUploadMut.isError && (
            <div className="text-red-600 text-sm">{(podUploadMut.error as any)?.response?.data?.error ?? (podUploadMut.error as Error)?.message ?? 'POD upload failed.'}</div>
          )}
          <button
            onClick={() => podUploadMut.mutate()}
            disabled={!selectedPodFile || podUploadMut.isPending}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {podUploadMut.isPending ? 'Uploading POD…' : 'Upload POD'}
          </button>
        </div>
      </div>

      {/* Actions */}
      {canPickup && (
        <div className="bg-white rounded-xl border p-5 mb-4">
          <h2 className="font-semibold text-gray-800 mb-3">Confirm Pickup</h2>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Vehicle loaded, gate 4…" />
          </div>
          {pickupMut.isError && (
            <div className="text-red-600 text-sm mb-3">{(pickupMut.error as any)?.response?.data?.error ?? 'Pickup failed.'}</div>
          )}
          <button onClick={() => pickupMut.mutate()} disabled={pickupMut.isPending}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60">
            {pickupMut.isPending ? 'Confirming pickup…' : '🚛 Confirm Pickup'}
          </button>
        </div>
      )}

      {canDeliver && !showDeliveryForm && (
        <div className="mb-4">
          <button onClick={() => setShowDeliveryForm(true)}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition-colors">
            📦 Confirm Delivery + POD
          </button>
        </div>
      )}

      {canDeliver && showDeliveryForm && (
        <div className="bg-white rounded-xl border p-5 mb-4">
          <h2 className="font-semibold text-gray-800 mb-3">Confirm Delivery</h2>
          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Notes (optional)</label>
              <input value={notes} onChange={e => setNotes(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Delivered, no damage" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Damage Notes (optional)</label>
              <input value={damageNote} onChange={e => setDamageNote(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Leave blank if no damage" />
            </div>
          </div>
          {deliverMut.isError && (
            <div className="text-red-600 text-sm mb-3">{(deliverMut.error as any)?.response?.data?.error ?? 'Delivery failed.'}</div>
          )}
          <div className="flex gap-3">
            <button onClick={() => deliverMut.mutate()} disabled={deliverMut.isPending}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60">
              {deliverMut.isPending ? 'Confirming delivery…' : '✅ Complete Delivery'}
            </button>
            <button onClick={() => setShowDeliveryForm(false)}
              className="px-5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {isDelivered && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
          <div className="text-3xl mb-2">✅</div>
          <p className="font-bold text-green-800 text-lg">Delivery Complete</p>
          <p className="text-green-600 text-sm mt-1">Delivery is complete. Your dispatcher will confirm settlement.</p>
          <button onClick={() => navigate('/driver/jobs')}
            className="mt-3 bg-green-700 text-white text-sm font-semibold px-5 py-2 rounded-lg hover:bg-green-800">
            Back to My Jobs
          </button>
        </div>
      )}
    </div>
  )
}
